import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';

import '@/index.scss';
import '@/utils/leafletSetup';
import App from '@/App.tsx';
import { PersistGate } from 'redux-persist/integration/react';
import { loadConfig } from './store/slices/appConfigSlice';
import { persistor, store } from './store/store';
import { ToastContainer } from 'react-toastify';
import clivLogo from '@/assets/icons/cliv-logo.png';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { initSentry } from './config/sentry';
import { installReactDomMutationGuard } from './utils/installReactDomMutationGuard';
import { installGlobalErrorHandlers } from './utils/globalErrorHandlers';
import { RootErrorFallback } from './components/error/RootErrorFallback';

import styles from './main.module.scss';
import { sendConfigToSW } from './serviceworker/mediator';
import { systemConfig } from './config/systemConfig';
import { applyDisplayLocalesFromConfig, initI18n } from './i18n';
import { APP_CONFIG_KEY } from './types/AppConfig.type';

installReactDomMutationGuard();
// Catch unhandled promise rejections and script errors so the app doesn't go blank without feedback
installGlobalErrorHandlers();

store.dispatch(loadConfig());
const unsubscribe = store.subscribe(() => {
  const state = store.getState();
  if (state.appConfig.configFetchState === 'success') {
    unsubscribe();
    void applyDisplayLocalesFromConfig({
      [APP_CONFIG_KEY.enableDisplayLanguages]:
        state.appConfig[APP_CONFIG_KEY.enableDisplayLanguages],
      [APP_CONFIG_KEY.displayLocales]: state.appConfig[APP_CONFIG_KEY.displayLocales],
    }).catch((error: unknown) => {
      console.error('Failed to apply display locales from config', error);
    });
    // Initialize Sentry with the current store state ONLY after the config is loaded
    initSentry(state.appConfig, systemConfig.version);
    sendConfigToSW(state.appConfig);
  }
});

void initI18n()
  .catch((error: unknown) => {
    console.error('Failed to initialise translations', error);
  })
  .then(() => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <Sentry.ErrorBoundary
          fallback={<RootErrorFallback />}
          onError={(error, componentStack) => {
            try {
              Sentry.captureException(error, { extra: { componentStack } });
            } catch {
              // Sentry may not be initialized yet
            }
            console.error('Root error boundary caught:', error, componentStack);
          }}
        >
          <ToastContainer
            position="top-center"
            autoClose={1500}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick={false}
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          <Provider store={store}>
            <PersistGate
              loading={
                <div className={styles.loadingPageContainer}>
                  <div className={styles.brandAssetContainer}>
                    <img src={clivLogo} className={styles.logo} alt="Logo" />
                    <div className={styles.loadingContainer}>
                      <div className={styles.loadingPageTextContainer}>
                        <span className={`${styles.titleHeader} ${styles.header}`}>
                          control-live
                        </span>
                        <span className={`${styles.titleHeader} ${styles.subHeader}`}>
                          wherever you are in the world
                        </span>
                      </div>
                      <Box sx={{ display: 'flex' }}>
                        <CircularProgress sx={{ color: 'white' }} size="4rem" thickness={4} />
                      </Box>
                    </div>
                  </div>
                </div>
              }
              persistor={persistor}
            >
              {/* <MsalProvider instance={msalInstance}> */}
              <App />
              {/* </MsalProvider> */}
            </PersistGate>
          </Provider>
        </Sentry.ErrorBoundary>
      </StrictMode>
    );
  });
